<template>
  <Teleport to="body">
  <div class="dark:text-gray-50">
    <div 
      @click.self="$emit('close')"  
      class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40">
      <div class="glass-card p-10 w-[80%] h-[800px] m-auto shadow-xl items-center text-center flex flex-col">

            <!-- Header with progress indicator -->
        <div class="mb-4 text-base flex space-x-2 justify-between items-center">
          <div class="flex items-center space-x-4">
            <span class="font-semibold">{{ currentPage.heading }}</span>
            <div class="text-sm text-gray-500">
              {{ currentPageIndex + 1 }} of {{ pages.length }}
            </div>
          </div>
          <button
            @click="$emit('close')"
            class="hover:bg-gray-100 dark:hover:bg-gray-800 p-1 rounded"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x">
              <path d="M18 6 6 18"/>
              <path d="m6 6 12 12"/>
            </svg>
          </button>
        </div>

        <!-- Progress bar -->
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-6">
          <div 
            class="bg-blue-600 h-2 rounded-full transition-all duration-300"
            :style="{ width: `${progressPercentage}%` }"
          ></div>
        </div>

        <div
          class="flex flex-col items-center text-center mb-6 space-y-10 bg-gray-100 dark:bg-gray-700 dark:text-gray-50 p-10 rounded-md m-auto min-w-[0%] max-h-[80%]"
          
        >
          <!-- Upper text content -->
          <div v-if="currentPage.upperText" class="mb-6 space-y-3">
            <p
              v-for="(paragraph, index) in currentPage.upperText"
              :key="'upper-' + index"
              class="space-y-2 text-gray-700 dark:text-gray-300"
            >
              {{ paragraph }}
            </p>
          </div>

          <!-- Image section -->
          <div
            v-if="currentPage.image"
            class="flex overflow-auto min-w-[60%] max-h-[400px] m-auto items-center text-center mb-6 bg-gray-100 dark:bg-gray-700 rounded"
          >
            <img
              :src="`${currentPage.image}`"
              :alt="currentPage.imageAlt || 'How-to guide image'"
              class="max-w-full max-h-full m-auto rounded"
            />
          </div>

          <!-- Lower text content -->
          <div v-if="currentPage.lowerText" class="mb-6 space-y-3">
            <ul class="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li
                v-for="(paragraph, index) in currentPage.lowerText"
                :key="'note-' + index"
              >
                {{ paragraph }}
              </li>
            </ul>
          </div>

          <div v-if="currentPage.notes" class="mb-6 space-y-3">
            <ul class="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li
                v-for="(paragraph, index) in currentPage.notes"
                :key="'note-' + index"
              >
                {{ paragraph }}
              </li>
            </ul>
          </div>
        </div>

        
        <!-- Navigation buttons -->
        <div class="flex justify-between items-center pt-4 border-gray-200 dark:border-gray-700 mt-auto dark:text-gray-50 text-gray-50 space-x-3">
          <button 
            v-if="currentPageIndex > 0"
            class="px-5 py-2 rounded-lg border bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700 transition"
            @click="previousPage"
          >
            Previous
          </button>
          <div v-else></div>

          <div class="flex space-x-4">
            <button 
              class="px-5 py-2 rounded-lg border bg-gray-600 hover:bg-gray-700 dark:hover:bg-gray-700 transition"
              @click="$emit('close')"
            >
              {{ isLastPage ? 'Close' : 'Cancel' }}
            </button>
            
            <button 
              v-if="!isLastPage"
              class="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition"
              @click="nextPage"
            >
              Next
            </button>
            
            <button 
              v-else
              class="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 transition"
              @click="$emit('complete')"
            >
              Complete
            </button>
          </div>
        </div>
      </div>
    </div>  
  </div>
  </Teleport>
</template>

<script>
import { ref, computed, watch } from "vue";

export default {
  name: "HowToGuide",
  emits: ['close', 'complete', 'page-change'],
  
  props: {
    pages: {
      type: Array,
      required: true,
      default: () => []
    },
    initialPage: {
      type: Number,
      default: 0
    }
  },

  setup(props, { emit }) {

    const currentPageIndex = ref(props.initialPage);


    const currentPage = computed(() => {
      return props.pages[currentPageIndex.value] || {};
    });

    const isLastPage = computed(() => {
      return currentPageIndex.value === props.pages.length - 1;
    });

    const progressPercentage = computed(() => {
      if (props.pages.length === 0) return 0;
      return ((currentPageIndex.value + 1) / props.pages.length) * 100;
    });


    const nextPage = () => {
      if (currentPageIndex.value < props.pages.length - 1) {
        currentPageIndex.value++;
        emit('page-change', currentPageIndex.value);
      }
    };

    const previousPage = () => {
      if (currentPageIndex.value > 0) {
        currentPageIndex.value--;
        emit('page-change', currentPageIndex.value);
      }
    };

    const goToPage = (pageIndex) => {
      if (pageIndex >= 0 && pageIndex < props.pages.length) {
        currentPageIndex.value = pageIndex;
        emit('page-change', currentPageIndex.value);
      }
    };

    watch(() => props.initialPage, (newPage) => {
      if (newPage !== currentPageIndex.value) {
        goToPage(newPage);
      }
    });

    return {
      currentPageIndex,
      currentPage,
      isLastPage,
      progressPercentage,
      nextPage,
      previousPage,
      goToPage,
    };
  }
};
</script>